import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Tabs, 
  Tab, 
  List, 
  ListItem, 
  ListItemText, 
  Divider,
  Chip,
  Link,
  Grid,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`pastoral-tabpanel-${index}`}
      aria-labelledby={`pastoral-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const SectorulPastoralLiturgic: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Sectorul Pastoral-Liturgic și pentru Misiune
      </Typography>
      
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="pastoral tabs">
          <Tab label="Departamente" />
          <Tab label="Personal" />
          <Tab label="Activități" />
          <Tab label="Resurse" />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Departamentul Pastoral-Liturgic
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Responsabil: Pr. Daniel Stîngă (San Bonifacio)
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Coordonarea serviciilor liturgice"
                      secondary="Organizarea și supravegherea serviciilor religioase"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Formarea liturgică"
                      secondary="Instruirea clerului și laicilor în practicile liturgice"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Calendarul liturgic"
                      secondary="Planificarea și organizarea sărbătorilor religioase"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Departamentul pentru Misiune
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Responsabil: Pr. Cristian Popescu (Roma)
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Activități misionare"
                      secondary="Organizarea și coordonarea activităților de evanghelizare"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Formarea misionară"
                      secondary="Instruirea și pregătirea misionarilor"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Proiecte sociale"
                      secondary="Implementarea proiectelor de ajutor social și spiritual"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Typography variant="h6" gutterBottom>
              Personalul Sectorului
            </Typography>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Departamentul Pastoral-Liturgic</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" color="primary">
                        Pr. Daniel Stîngă
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Locație: San Bonifacio
                      </Typography>
                      <Typography variant="body2">
                        Responsabil pentru coordonarea activităților pastorale și liturgice
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
            
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>Departamentul pentru Misiune</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                      <Typography variant="subtitle1" color="primary">
                        Pr. Cristian Popescu
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Locație: Roma
                      </Typography>
                      <Typography variant="body2">
                        Responsabil pentru activitățile misionare și evanghelizare
                      </Typography>
                    </Paper>
                  </Grid>
                </Grid>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Activități Pastorale
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Servicii liturgice săptămânale"
                      secondary="Organizarea serviciilor religioase în toate parohiile"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Sărbători religioase"
                      secondary="Celebrarea sărbătorilor ortodoxe importante"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Sacramente"
                      secondary="Administrarea botezului, cununiei și altor sacramente"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Activități Misionare
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Evanghelizare"
                      secondary="Promovarea mesajului creștin în comunitate"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Formare religioasă"
                      secondary="Cursuri și seminarii de formare spirituală"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Proiecte sociale"
                      secondary="Ajutor pentru cei nevoiași și marginalizați"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Resurse Liturgice
                </Typography>
                <List>
                  <ListItem>
                    <ListItemText 
                      primary="Cărți de slujbă"
                      secondary="Textul slujbelor și rugăciunilor"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Muzică bisericească"
                      secondary="Cântări și imnuri religioase"
                    />
                  </ListItem>
                  <Divider />
                  <ListItem>
                    <ListItemText 
                      primary="Materiale de formare"
                      secondary="Ghiduri și manuale pentru cler și laici"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom color="primary">
                  Statut Sector
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                  <Chip label="Activ" color="success" />
                  <Chip label="Pastoral" color="primary" />
                  <Chip label="Liturgic" color="secondary" />
                </Box>
                <Typography variant="body2">
                  Sectorul Pastoral-Liturgic și pentru Misiune coordonează toate 
                  activitățile religioase, liturgice și misionare ale eparhiei, 
                  asigurând continuitatea tradiției ortodoxe și promovarea 
                  mesajului creștin în comunitate.
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
};

export default SectorulPastoralLiturgic;
